import { useEffect, useRef, useState } from "react";

interface TermsAgreementModalProps {
  isOpen: boolean;
  isSubmitting: boolean;
  onAgree: () => void;
  onDecline: () => void;
}

const TermsAgreementModal = ({
  isOpen,
  isSubmitting = false,
  onAgree,
  onDecline,
}: TermsAgreementModalProps) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      setHasScrolledToBottom(false);
    }
  }, [isOpen]);

  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;
    if (isAtBottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity"></div>

        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>

        <div className="inline-block align-bottom bg-white rounded-2xl px-6 pt-6 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full sm:p-8">
          <div className="sm:flex sm:items-start">
            <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
              <svg
                className="h-6 w-6 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                />
              </svg>
            </div>
            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
              <h3 className="text-2xl leading-6 font-bold text-gray-900 mb-4">
                Terms of Service and Waiver of Liability
              </h3>

              <div
                ref={scrollContainerRef}
                className="mt-4 max-h-96 overflow-y-auto border border-gray-200 rounded-lg p-4 bg-gray-50"
                onScroll={handleScroll}
              >
                <div className="text-sm text-gray-700 space-y-6">
                  <p className="leading-relaxed font-medium">
                    <strong>Welcome to the Published Offers Feature.</strong>{" "}
                    Before accessing this area, please read and agree to the
                    following terms of service and waiver of liability:
                  </p>

                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2 text-base">
                        1. Terms of Service
                      </h4>

                      <div className="space-y-3 ml-4">
                        <div className="prose">
                          <p>Comfy Technology Ltd(CTL) is hereby excluded from any liability arising from the actions of Users of its platform CRBS,placeIT & KAVA in offering a risk to a reinsurer or participating on a risk offered by a reinsurance broker and or insurer.</p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2 text-base">
                        2. Waiver of Liability
                      </h4>

                      <div className="space-y-3 ml-4">
                        <div className="prose">
                          <h1>Comfy Technology Ltd. (CTL) - CRBS Waiver of Liability</h1>
                          <p><strong>Specific Functionality Focus:</strong> Ability to publish offers to Kava Marketplace</p>
                          <p><strong>Effective Date:</strong> June 21, 2025</p>

                          <p>By using the feature within the Comfy Reinsurance Brokerage System (CRBS) that allows you to publish offers to the Kava Marketplace, you acknowledge and agree to the following Waiver of Liability. This waiver is an integral part of your agreement to use this specific functionality.</p>

                          <h2>1. Acknowledgment of Risk in Publishing Offers</h2>
                          <p>You understand that the act of publishing reinsurance facultative and treaty business offers to a marketplace like Kava involves inherent risks, including but not limited to:</p>
                          <ul>
                            <li>The accuracy and completeness of the information you provide in the offer.</li>
                            <li>The potential for misinterpretation or misuse of the published information by third parties on Kava.</li>
                            <li>The commercial terms and legal implications of any reinsurance business that may arise from a published offer.</li>
                            <li>The inability to control how reinsurers or other parties on Kava react to or process your published offer.</li>
                          </ul>

                          <h2>2. Exclusion of Liability for Publishing Actions</h2>
                          <p>Comfy Technology Ltd. (CTL) is hereby expressly excluded from any and all liability, claims, demands, actions, and causes of action whatsoever, whether in law or in equity, arising from your actions, inactions, omissions, or negligence when publishing business offers via CRBS to the Kava Marketplace, including, but not limited to:</p>
                          <ul>
                            <li>Any errors, omissions, or inaccuracies in the data or information contained within the offers you publish.</li>
                            <li>Any decisions made by third parties on the Kava Marketplace based on the offers you publish.</li>
                            <li>Any financial losses, damages, or liabilities incurred by you or any third party as a result of an offer being published, viewed, reviewed, accepted, or otherwise acted upon on the Kava Marketplace.</li>
                            <li>Any consequences arising from the content, terms, or nature of the reinsurance business offers you choose to publish.</li>
                          </ul>

                          <h2>3. No Guarantee of Offer Outcomes</h2>
                          <p>CTL makes no representations or warranties regarding the outcome or success of any business offer you publish through CRBS to the Kava Marketplace. The decision to publish an offer, and the subsequent response from any reinsurer or party on Kava, rests solely with you and the Kava users, respectively. CTL is not responsible for the commercial terms, negotiation, or results of any transactions arising from your published offers.</p>

                          <h2>4. User Responsibility for Published Content</h2>
                          <p>You acknowledge and agree that you are solely responsible for:</p>
                          <ul>
                            <li>Ensuring the accuracy, completeness, and legality of all data and information within any reinsurance business offer you publish to the Kava Marketplace via CRBS.</li>
                            <li>Complying with all applicable laws, regulations, and industry standards related to the content and publication of your reinsurance offers.</li>
                            <li>Conducting your own due diligence regarding any potential reinsurer or party on Kava who may respond to your published offer.</li>
                            <li>Maintaining the confidentiality and security of your CRBS account credentials related to the publishing feature.</li>
                          </ul>

                          <h2>5. Indemnification</h2>
                          <p>You agree to indemnify, defend, and hold harmless CTL, its affiliates, licensors, and service providers, and its and their respective officers, directors, employees, contractors, agents, licensors, suppliers, successors, and assigns from and against any claims, liabilities, damages, judgments, awards, losses, costs, expenses, or fees (including reasonable attorneys' fees) arising out of or relating to your violation of these Terms or your use of the CRBS publishing feature, including, but not limited to, the content of your published offers, your use of this feature's content, services, and products other than as expressly authorized in these Terms, or your use of any information obtained from this feature.</p>

                          <p><strong>By proceeding to use the publishing feature within CRBS, you indicate your explicit agreement to this Waiver of Liability.</strong></p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 p-4 bg-red-50 border-l-4 border-red-400 rounded">
                    <p className="leading-relaxed">
                      <strong className="text-red-800">
                        IMPORTANT ACKNOWLEDGMENT:
                      </strong>
                      <span className="text-red-700">
                        {" "}
                        By clicking "I Agree to Terms" below, you acknowledge
                        that you have read, understood, and agree to be bound by
                        these terms of service and waiver of liability. You
                        understand that this agreement affects your legal rights
                        and remedies. If you do not agree to these terms, you
                        must click "Cancel" and will be redirected away from
                        this section.
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              {!hasScrolledToBottom && (
                <div className="mt-4 flex items-center justify-center text-sm text-gray-500">
                  <svg
                    className="w-4 h-4 mr-2 animate-bounce"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 14l-7 7m0 0l-7-7m7 7V3"
                    />
                  </svg>
                  Please scroll to the bottom to continue
                </div>
              )}
            </div>
          </div>

          <div className="mt-8 flex flex-col-reverse sm:flex-row sm:justify-end space-y-3 space-y-reverse sm:space-y-0 sm:space-x-3">
            <button
              type="button"
              className="w-full inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-6 py-3 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:w-auto sm:text-sm transition-all duration-200"
              onClick={onDecline}
            >
              Cancel
            </button>
            <button
              type="button"
              className={`w-full inline-flex justify-center rounded-lg border border-transparent shadow-sm px-6 py-3 text-base font-medium sm:w-auto sm:text-sm transition-all duration-200 ${hasScrolledToBottom
                ? "bg-green-600 text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transform hover:scale-105"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
              onClick={hasScrolledToBottom ? onAgree : undefined}
              disabled={!hasScrolledToBottom || isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "I Agree to Terms"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsAgreementModal;
